import Button from "@bmi-digital/components/button";
import PromoSection from "@bmi-digital/components/promo-section";
import Typography from "@bmi-digital/components/typography";
import { graphql, Link } from "gatsby";
import React from "react";
import { useTheme } from "@mui/material/styles";
import FallbackComponent from "../components/FallbackComponent";
import Page from "../components/Page";
import { getPathWithCountryCode } from "../utils/path";
import { stringifyToObject } from "../utils/createActionLabelForAnalytics";
import createImageProps from "../components/image/createImageProps";
import createVideoProps from "../components/video/createVideoProps";
import { HomePageData, SiteData } from "../components/Site";
import type { HouseType } from "../components/visualiser/Types";
import type { MicroCopy as MicroCopyData } from "../components/MicroCopy";
import type { AnchorLinkActionProps } from "@bmi-digital/components/anchor-link";
import type { PayloadNotFoundError } from "../types/payload";
import type { VideoVillain } from "../components/video/types";
import type { ImageVillain } from "../components/image/types";
import type { CalculatorConfig } from "../components/pitched-roof-calculator/types";
import type { GeneralErrorData } from "../templates/general-error";
import type { CountryNavigation, SampleOrder } from "../components/Header";
import type { Visualiser } from "../components/Visualiser";

type Cta = Pick<PayloadNotFoundError["cta"][0], "label"> & {
  linkedPage: {
    value: Pick<PayloadNotFoundError["cta"][0]["linkedPage"]["value"], "path">;
  };
};

export type NotFoundErrorData = Pick<
  PayloadNotFoundError,
  "title" | "subtitle"
> & {
  cta: [Cta] | never[];
  featuredVideo: VideoVillain | null;
  featuredMedia: ImageVillain | null;
};

type Data = {
  payloadNotFoundError: NotFoundErrorData;
  payloadSite: SiteData;
  allPayloadMicroCopy: {
    nodes: MicroCopyData[];
  };
  payloadPitchedRoofCalculatorConfig: CalculatorConfig | null;
  allPayloadHouseModel: {
    nodes: HouseType[];
  };
  payloadHomePage: HomePageData;
  payloadGeneralError: GeneralErrorData;
  payloadCountryNavigation: CountryNavigation;
  payloadSampleOrder: SampleOrder | null;
  payloadVisualiser: Visualiser | null;
};

const Cta = (props: Cta & { countryCode: string }) => {
  const actionProps: AnchorLinkActionProps = {
    to: getPathWithCountryCode(props.countryCode, props.linkedPage.value.path),
    component: Link
  };

  const ctaLabel = props.label;
  return (
    <Button
      {...actionProps}
      data-testid="404-page-cta"
      gtm={{
        id: "cta-click1",
        action: stringifyToObject(actionProps.to) || actionProps.href,
        label: ctaLabel
      }}
    >
      {ctaLabel}
    </Button>
  );
};

const FourOFour = ({ data }: { data: Data }) => {
  const theme = useTheme();

  const siteData = data.payloadSite;
  const errorFourOFour = data.payloadNotFoundError;

  if (!siteData || !errorFourOFour) {
    return <FallbackComponent />;
  }

  return (
    <Page
      title={errorFourOFour.title}
      pageData={{
        breadcrumbs: null,
        signupBlock: null,
        meta: null,
        path: null
      }}
      siteData={siteData}
      microCopy={data.allPayloadMicroCopy.nodes}
      pitchedRoofCalculatorConfig={data.payloadPitchedRoofCalculatorConfig}
      visualiserHouseTypes={data.allPayloadHouseModel.nodes}
      homePage={data.payloadHomePage}
      generalError={data.payloadGeneralError}
      countryNavigation={data.payloadCountryNavigation}
      sampleOrder={data.payloadSampleOrder}
      visualiser={data.payloadVisualiser}
    >
      <PromoSection
        title={errorFourOFour.title}
        media={
          errorFourOFour.featuredVideo
            ? createVideoProps(errorFourOFour.featuredVideo, theme)
            : errorFourOFour.featuredMedia
              ? createImageProps(errorFourOFour.featuredMedia, theme)
              : undefined
        }
      >
        <Typography variant="body2" gutterBottom>
          {errorFourOFour.subtitle}
        </Typography>
        {errorFourOFour.cta[0] && (
          <Cta {...errorFourOFour.cta[0]} countryCode={siteData.countryCode} />
        )}
      </PromoSection>
    </Page>
  );
};

export default FourOFour;

export const pageQuery = graphql`
  query NotFoundPage {
    payloadNotFoundError {
      title
      subtitle
      cta {
        label
        linkedPage {
          value {
            ... on PayloadBrandLandingPage {
              path
            }
            ... on PayloadContactUsPage {
              path
            }
            ... on PayloadCookiePolicyPage {
              path
            }
            ... on PayloadDocumentLibraryPage {
              path
            }
            ... on PayloadHomePage {
              path
            }
            ... on PayloadProductListerPage {
              path
            }
            ... on PayloadSimplePage {
              path
            }
            ... on PayloadTrainingListerPage {
              path
            }
          }
        }
      }
      featuredMedia {
        ...ImageVillainFragment
      }
      featuredVideo {
        ...VideoVillainFragment
      }
    }
    payloadSite {
      ...SiteFragment
    }
    allPayloadMicroCopy {
      nodes {
        ...MicroCopyFragment
      }
    }
    payloadPitchedRoofCalculatorConfig {
      ...PitchedRoofCalculatorFragment
    }
    allPayloadHouseModel {
      nodes {
        ...VisualiserHouseFragment
      }
    }
    payloadHomePage {
      ...HomePageFragment
    }
    payloadGeneralError {
      ...GeneralErrorFragment
    }
    payloadCountryNavigation {
      ...CountryNavigationFragment
    }
    payloadSampleOrder {
      ...SampleOrderFragment
    }
    payloadVisualiser {
      ...VisualiserFragment
    }
  }
`;
